import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { router } from '@inertiajs/react'
import { OrganizationLayout } from '../../layouts/organization-layout'
import { getDateLocale } from '@/lib/i18n'
import { Button } from '../../components/ui/button'
import { Separator } from '../../components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select'
import { ArrowUpRight, ArrowDownRight, ArrowLeftIcon } from 'lucide-react'
import { getTypeBadge, getDetails, ALL_TRANSACTION_TYPES } from '../../components/credit-transactions/transaction-helpers'

interface Organization {
  id: number
  name: string
}

interface Transaction {
  id: number
  transaction_type: string
  credits_amount: number
  metadata: Record<string, unknown> | null
  created_at: string
  user_email: string | null
  creditable_type: string | null
  creditable_id: number | null
}

interface Pagination {
  page: number
  count: number
  pages: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface Filters {
  type: string
}

interface CreditTransactionsProps {
  organization: Organization
  transactions: Transaction[]
  pagy: Pagination
  filters: Filters
}

function CreditTransactions({ organization, transactions, pagy, filters }: CreditTransactionsProps) {
  const { t } = useTranslation('organizations')
  const [typeFilter, setTypeFilter] = useState(filters.type || 'all')

  const formatDate = (isoString: string) => {
    const date = new Date(isoString)
    return date.toLocaleDateString(getDateLocale(), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleFilter = () => {
    const params: Record<string, string> = {}
    if (typeFilter !== 'all') params.type = typeFilter
    router.get('/organization/credit_transactions', params, { preserveState: true })
  }

  const handleReset = () => {
    setTypeFilter('all')
    router.get('/organization/credit_transactions', {}, { preserveState: true })
  }

  const goToPage = (page: number) => {
    const params = new URLSearchParams(window.location.search)
    params.set('page', page.toString())
    router.get(`/organization/credit_transactions?${params.toString()}`, {}, { preserveState: true })
  }

  const totalDebits = transactions
    .filter(t => t.credits_amount < 0)
    .reduce((sum, t) => sum + Math.abs(t.credits_amount), 0)

  const totalCredits = transactions
    .filter(t => t.credits_amount > 0)
    .reduce((sum, t) => sum + t.credits_amount, 0)

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col">
        <div className="px-4 py-6 lg:px-6">
          <div className="mx-auto max-w-4xl space-y-8">

            {/* Back link */}
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 text-muted-foreground"
              onClick={() => router.visit('/organization/subscription')}
            >
              <ArrowLeftIcon className="size-4" />
              {t('creditTransactions.backButton')}
            </Button>

            {/* Summary section */}
            {transactions.length > 0 && (
              <>
                <div>
                  <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('creditTransactions.summary.title', { defaultValue: 'Summary' })}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t('creditTransactions.pageDescription', { organizationName: organization.name, count: pagy.count })}
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div className="rounded-lg border p-4">
                    <p className="text-xs font-medium text-muted-foreground">{t('creditTransactions.summary.showing')}</p>
                    <p className="mt-1 text-2xl font-semibold">{t('creditTransactions.summary.showingFormat', { shown: transactions.length, total: pagy.count })}</p>
                  </div>
                  <div className="rounded-lg border p-4">
                    <p className="text-xs font-medium text-muted-foreground">{t('creditTransactions.summary.creditsUsed')}</p>
                    <p className="mt-1 flex items-center text-2xl font-semibold text-red-600">
                      <ArrowDownRight className="mr-1 size-5" />
                      {totalDebits.toFixed(2)}
                    </p>
                  </div>
                  <div className="rounded-lg border p-4">
                    <p className="text-xs font-medium text-muted-foreground">{t('creditTransactions.summary.creditsAdded')}</p>
                    <p className="mt-1 flex items-center text-2xl font-semibold text-green-600">
                      <ArrowUpRight className="mr-1 size-5" />
                      {totalCredits.toFixed(2)}
                    </p>
                  </div>
                </div>

                <Separator />
              </>
            )}

            {/* Filters */}
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                {t('creditTransactions.filters.title', { defaultValue: 'Filter Transactions' })}
              </h2>
              <div className="mt-4 flex items-end gap-3 flex-wrap">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-foreground">{t('creditTransactions.filters.type')}</label>
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('creditTransactions.filters.allTypes')}</SelectItem>
                      {ALL_TRANSACTION_TYPES.map(type => (
                        <SelectItem key={type} value={type}>{t(`creditTransactions.types.${type}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button onClick={handleFilter} className="max-sm:w-full">{t('creditTransactions.filters.filterButton')}</Button>
                <Button variant="outline" onClick={handleReset} className="max-sm:w-full">{t('creditTransactions.filters.resetButton')}</Button>
              </div>
            </div>

            <Separator />

            {/* Transactions Table */}
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('creditTransactions.table.date')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('creditTransactions.table.type')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('creditTransactions.table.credits')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('creditTransactions.table.details')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('creditTransactions.table.user')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {transactions.length > 0 ? (
                    transactions.map((transaction) => (
                      <tr key={transaction.id} className="hover:bg-muted/50 transition-colors">
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          {formatDate(transaction.created_at)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {getTypeBadge(transaction.transaction_type, t)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`text-sm font-semibold ${
                            transaction.credits_amount < 0 ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {transaction.credits_amount < 0 ? '' : '+'}
                            {transaction.credits_amount.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {getDetails(transaction, t)}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {transaction.user_email || '—'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-muted-foreground">
                        <p>{t('creditTransactions.empty.message')}</p>
                        <p className="mt-2 text-xs">
                          {typeFilter !== 'all'
                            ? t('creditTransactions.empty.filterHint')
                            : t('creditTransactions.empty.defaultHint')}
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Pagination */}
              {pagy.pages > 1 && (
                <div className="flex items-center justify-between gap-3 border-t px-4 py-3 max-sm:flex-col">
                  <p className="text-sm text-muted-foreground text-nowrap">
                    {t('creditTransactions.pagination.showing')}{' '}
                    <span className="font-medium">{pagy.from}</span> {t('creditTransactions.pagination.to')}{' '}
                    <span className="font-medium">{pagy.to}</span> {t('creditTransactions.pagination.of')}{' '}
                    <span className="font-medium">{pagy.count}</span> {t('creditTransactions.pagination.transactions')}
                  </p>
                  <div className="flex gap-2">
                    {pagy.prev && (
                      <Button variant="outline" size="sm" onClick={() => goToPage(pagy.prev!)}>
                        {t('creditTransactions.pagination.previous')}
                      </Button>
                    )}
                    {pagy.next && (
                      <Button variant="outline" size="sm" onClick={() => goToPage(pagy.next!)}>
                        {t('creditTransactions.pagination.next')}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  )
}

CreditTransactions.layout = (page: React.ReactNode) => <OrganizationLayout>{page}</OrganizationLayout>

export default CreditTransactions
